import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, CircleDot, CircleAlert, ChevronLeft, ChevronRight, HardHat, Pencil, Printer, Briefcase, CaseSensitive, User } from "lucide-react";
import { useGroupSort, GroupHeader } from "@/components/shared/GroupSort";
import { useIsMobile, useIsNarrow } from "@/components/shared/PagePrimitives";
import { ListHeader, ListToolbar } from "@/components/shared/ListToolbar";
import { useListFilters } from "@/hooks/useListFilters";
import { useExportDownload } from "@/hooks/useExportDownload";
import { personColor } from "@/lib/taskCalendar";
import { InfoTip } from "@/components/settings/Grouped";

const MONTHS = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];
const DOW = ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"];
const pad = (n) => String(n).padStart(2, "0");
const asDate = (iso) => new Date(`${iso}T00:00:00`);
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (iso, n) => { const d = asDate(iso); d.setDate(d.getDate() + n); return ymd(d); };
const dLabel = (iso) => { const d = asDate(iso); return `${DOW[d.getDay()]} ${d.getDate()}. ${d.getMonth() + 1}.`; };
const daysWord = (n) => (n === 1 ? "den" : n >= 2 && n <= 4 ? "dny" : "dní");
const EMPTY = { days: [], projects: [], today: "" };
// 351: jednotná lišta — záložky Všechny / Chybí zápis / Moje, Filtr Zakázka, hledání; Tisk v ⋯.
// 378: Zobrazení (V) — Seskupit (Zápisy / Moje a ostatní) a Řadit (Zakázka / Chybějící zápisy); nezahájené zakázky vždy dole.
const DIARY_GROUPS = {
  zapisy: { label: "Zápisy", icon: CircleAlert, key: (p) => (p.not_started ? "c" : p.missing ? "a" : "b"), order: (a, b) => a.localeCompare(b),
    name: (k) => ({ a: "Chybí zápis", b: "Vše zapsáno", c: "Nezahájené" }[k]), dirs: ["Chybí zápis první", "Obráceně"] },
  mine: { label: "Moje a ostatní", icon: User, key: (p) => (p.mine ? "a" : "b"), order: (a, b) => a.localeCompare(b),
    name: (k) => (k === "a" ? "Moje zakázky" : "Ostatní zakázky"), dirs: ["Moje první", "Ostatní první"] },
};
const DIARY_ORDERS = {
  name: { label: "Zakázka", icon: CaseSensitive, dirs: ["A → Z", "Z → A"], def: "asc", val: (p) => p.name || "" },
  missing: { label: "Chybějící zápisy", icon: CircleAlert, dirs: ["Od nejmenšího", "Od největšího"], def: "desc", val: (p) => p.missing },
};
const notStartedLast = (p) => (p.not_started ? 1 : 0);
const byDiaryName = (a, b) => (a.name || "").localeCompare(b.name || "", "cs");

// Stav dne z GET /diary/overview (302): ikona + barva; „je z čeho psát“ = čárkovaný rámeček.
const STATE = {
  done: { label: "Zapsáno", icon: Check, cls: "bg-success/15 text-success" },
  draft: { label: "Rozepsáno", icon: CircleDot, cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400" },
  pending: { label: "Je z čeho psát" },
  missing: { label: "Chybí zápis", icon: CircleAlert, cls: "bg-destructive/10 text-destructive" },
  today: { label: "Dnes zatím bez zápisu" },
  weekend: { label: "Víkend" },
  off: { label: "Mimo období stavby" },
};
const LEGEND = ["done", "draft", "pending", "missing"];

function Mark({ state, small = false }) {
  const s = STATE[state] || {};
  const box = small ? "w-6 h-6 rounded-[7px]" : "w-7 h-7 rounded-[8px]";
  if (state === "pending") return <span className={`${box} block border-[1.5px] border-dashed border-muted-foreground/70`} />;
  if (state === "weekend") return <span className={`${box} grid place-items-center`}><i className="w-1 h-1 rounded-full bg-muted-foreground/50" /></span>;
  if (state === "off") return <span className={`${box} grid place-items-center`}><i className="w-3 h-[2px] rounded bg-border" /></span>;
  if (!s.icon) return <span className={`${box} block`} />;
  return <span className={`${box} grid place-items-center ${s.cls}`}><s.icon size={small ? 14 : 16} strokeWidth={2.2} /></span>;
}

function Thumb({ id, small = false }) {
  return (
    <span className={`${small ? "w-7 h-7 rounded-[8px]" : "w-9 h-9 rounded-[10px]"} grid place-items-center text-white shrink-0`} style={{ background: personColor(id) }}>
      <HardHat size={small ? 15 : 18} />
    </span>
  );
}

function MissingPill({ p }) {
  if (p.not_started) return <span className="text-[13px] text-muted-foreground whitespace-nowrap">nezahájeno</span>;
  if (!p.missing) return <span className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-success/15 text-success text-[13px] font-semibold whitespace-nowrap"><Check size={14} />vše</span>;
  return <span className="inline-flex items-center h-7 px-2.5 rounded-full bg-destructive/10 text-destructive text-[13px] font-semibold whitespace-nowrap tabular-nums">{p.missing} {daysWord(p.missing)}</span>;
}

function Legend({ className = "" }) {
  return (
    <div className={`flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-muted-foreground ${className}`} data-testid="diary-legend">
      {LEGEND.map((k) => <span key={k} className="inline-flex items-center gap-1.5"><Mark state={k} small />{STATE[k].label}</span>)}
    </div>
  );
}

// ---------- telefon ----------
function WeekNav({ title, onPrev, onNext, canNext, onToday, extra }) {
  const b = "w-9 h-9 rounded-full bg-card border border-border grid place-items-center text-muted-foreground disabled:opacity-30";
  return (
    <div className="flex items-center gap-2 mb-2.5 px-1">
      <h2 className="text-[15px] font-bold" data-testid="diary-window-title">{title}</h2>
      {extra}
      <span className="mr-auto" />
      {onToday && <button type="button" onClick={onToday} className="h-9 px-3 rounded-full bg-card border border-border text-[13px] font-semibold">Dnes</button>}
      <button type="button" onClick={onPrev} aria-label="Předchozí týden" data-testid="diary-prev" className={b}><ChevronLeft size={17} /></button>
      <button type="button" onClick={onNext} disabled={!canNext} aria-label="Další týden" data-testid="diary-next" className={b}><ChevronRight size={17} /></button>
    </div>
  );
}

function IdleList({ idle, openDay }) {
  if (!idle.length) return null;
  return (
    <p className="mt-4 px-1 text-[13px] text-muted-foreground leading-relaxed" data-testid="diary-idle">
      <span className="font-semibold">Nezahájené: </span>
      {idle.map((p, i) => (
        <span key={p.project_id}><button type="button" onClick={() => openDay(p.project_id)} className="underline underline-offset-2 decoration-border">{p.name}</button>{i < idle.length - 1 ? ", " : ""}</span>
      ))}
    </p>
  );
}

// Telefon: týdenní mřížka — nahoře dny, pod každou zakázkou 7 políček přesně pod nimi.
// Týden se posouvá i prstem: dny a políčka všech zakázek jedou s prstem (vedle už je
// předchozí / další týden), názvy zakázek zůstávají na místě.
const SLIDE = "transform .22s ease-out";
function DayHead({ d, today }) {
  return (
    <div className={`text-center text-[11px] font-semibold leading-tight rounded-[8px] py-1 ${d === today ? "bg-brand/10 text-brand" : "text-muted-foreground"}`}>
      {DOW[asDate(d).getDay()]}<br /><span className="tabular-nums text-[13px]">{asDate(d).getDate()}.</span>
    </div>
  );
}
// tři týdny vedle sebe (předchozí | vidět | další), posunuté o šířku jednoho
function Track({ dx, tr, children }) {
  return (
    <div className="overflow-hidden">
      <div data-days className="flex w-[300%]" style={{ transform: `translateX(calc(-100% / 3 + ${dx}px))`, transition: tr }}>
        {children.map((c, i) => <div key={i} className="w-1/3 shrink-0 grid grid-cols-7 gap-1">{c}</div>)}
      </div>
    </div>
  );
}
function MobileGrid({ rows, idle, shown, prevDays, nextDays, today, openDay, nav7, onPrev, onNext, canNext, groups = null, closed, onToggle }) {
  const card = useRef(null);
  const touch = useRef(null);
  const swiped = useRef(false);
  const [dx, setDx] = useState(0);
  const [tr, setTr] = useState(SLIDE);
  const prevFirst = useRef(shown[0]);
  const still = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  // po šipce nový týden přijede ze strany (po přejetí prstem už je na místě)
  useEffect(() => {
    const was = prevFirst.current;
    prevFirst.current = shown[0];
    if (swiped.current) { swiped.current = false; return; }
    if (!was || !shown[0] || was === shown[0] || !card.current || still()) return;
    const from = shown[0] < was ? -48 : 48;
    card.current.querySelectorAll("[data-days]").forEach((el) => el.animate(
      [{ translate: `${from}px`, opacity: 0.3 }, { translate: "0px", opacity: 1 }], { duration: 220, easing: "ease-out" }));
  }, [shown[0]]); // eslint-disable-line react-hooks/exhaustive-deps
  const canPrev = prevDays.length > 0;
  const moved = useRef(false);   // po tažení prstem se den neotevře
  const onTouchStart = (e) => { const t = e.touches[0]; touch.current = { x: t.clientX, y: t.clientY, lock: null }; moved.current = false; setTr("none"); };
  const onTouchMove = (e) => {
    const s = touch.current; if (!s) return;
    const t = e.touches[0], mx = t.clientX - s.x, my = t.clientY - s.y;
    if (!s.lock && (Math.abs(mx) > 8 || Math.abs(my) > 8)) s.lock = Math.abs(mx) > Math.abs(my) ? "x" : "y";
    if (s.lock !== "x") return;
    moved.current = true;
    setDx((mx < 0 && !canNext) || (mx > 0 && !canPrev) ? mx / 4 : mx);   // kde další týden není, jen pruží
  };
  const onTouchEnd = () => {
    const s = touch.current; touch.current = null;
    const w = card.current?.querySelector("[data-days]")?.parentElement?.clientWidth || 320;
    const go = s?.lock === "x" && (dx > 60 && canPrev ? 1 : dx < -60 && canNext ? -1 : 0);
    if (!go) { setTr(SLIDE); setDx(0); return; }
    const done = () => { swiped.current = true; setTr("none"); setDx(0); (go > 0 ? onPrev : onNext)(); };
    if (still()) { done(); return; }
    setTr(SLIDE); setDx(go * w);   // dojede týden do konce, pak se vymění data
    setTimeout(done, 220);
  };
  const empty = Array.from({ length: 7 }, (_, i) => <span key={i} />);
  const heads = (list) => (list.length ? list.map((d) => <DayHead key={d} d={d} today={today} />) : empty);
  const marks = (list, cells) => (list.length ? list.map((d, i) => (
    <span key={d} className="h-10 grid place-items-center"><Mark state={cells[i]} /></span>)) : empty);
  return (
    <section data-testid="diary-mobile">
      {nav7}
      <div ref={card} className="bg-card border border-border rounded-[20px] overflow-hidden touch-pan-y select-none"
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} onTouchCancel={onTouchEnd}
        onClickCapture={(e) => { if (moved.current) { moved.current = false; e.preventDefault(); e.stopPropagation(); } }} data-testid="diary-swipe">
        <div className="px-3 pt-2.5 pb-2 border-b border-border">
          <Track dx={dx} tr={tr}>{[heads(prevDays), heads(shown), heads(nextDays)]}</Track>
        </div>
        {(groups ? groups.flatMap((g) => [<GroupHeader key={`g-${g.key}`} g={g} closed={closed?.has(g.key)} onToggle={onToggle} mobile />, ...(closed?.has(g.key) ? [] : g.rows)]) : rows).map((p) => (p.project_id ? (
          <div key={p.project_id} className="px-3 pt-3 pb-2 border-b border-border last:border-0" data-testid={`diary-row-${p.project_id}`}>
            <button type="button" onClick={() => openDay(p.project_id)} className="w-full flex items-center gap-2.5 text-left">
              <Thumb id={p.project_id} small />
              <span className="min-w-0 flex-1 truncate text-[15px] font-semibold">{p.name}</span>
              {p.missing ? <span className="shrink-0 inline-flex items-center h-6 px-2 rounded-full bg-destructive/10 text-destructive text-[12px] font-semibold tabular-nums">chybí {p.missing}</span>
                : <span className="shrink-0 w-6 h-6 rounded-full bg-success/15 text-success grid place-items-center" title="Vše zapsáno"><Check size={14} /></span>}
            </button>
            <div className="mt-1.5">
              <Track dx={dx} tr={tr}>{[
                marks(prevDays, p.prev),
                shown.map((d, i) => (
                  <button key={d} type="button" disabled={p.cells[i] === "off"} onClick={() => openDay(p.project_id, d)}
                    aria-label={`${p.name} — ${STATE[p.cells[i]]?.label} · ${dLabel(d)}`} data-testid={`diary-cell-${p.project_id}-${d}`}
                    className={`h-10 grid place-items-center rounded-[10px] ${d === today ? "bg-brand/5" : ""}`}>
                    <Mark state={p.cells[i]} />
                  </button>
                )),
                marks(nextDays, p.next),
              ]}</Track>
            </div>
          </div>
        ) : p))}
      </div>
      <IdleList idle={idle} openDay={openDay} />
    </section>
  );
}

/**
 * Stavební deník — přehled (302, podle obrazovky „Asana“): rozjeté zakázky × posledních 14 dní,
 * u každé kolik dní chybí. Klik na den otevře deník zakázky na ten den. Telefon: posledních 7 dní.
 */
export default function DiaryOverview() {
  const nav = useNavigate();
  const mobile = useIsMobile();
  const narrow = useIsNarrow();   // telefon i tablet: posledních 7 dní, PC 14
  const [end, setEnd] = useState(null);          // null = okno končí dneškem
  const [view, setView] = useState("all");       // all | missing | mine
  const [search, setSearch] = useState("");
  const { busy: printing, run: runPrint } = useExportDownload();

  // telefon: okno o týden dál na obě strany (předchozí a další týden se při tažení prstem ukážou hned)
  const windowOf = (e) => (mobile ? { from: addDays(e, -13), to: addDays(e, 7) } : { from: addDays(e, -13), to: e });
  const range = end ? windowOf(end) : null;
  // Pamatuje si poslední načtení — po návratu z deníku zakázky je přehled hned vidět a tiše se obnoví.
  const load = (r) => api.get("/diary/overview", { params: r ? { date_from: r.from, date_to: r.to } : {} }).then((x) => x.data);
  const q = useQuery({ queryKey: ["diary-overview", range?.from || "", range?.to || ""], staleTime: 0, placeholderData: (prev) => prev, queryFn: () => load(range) });
  useEffect(() => { if (q.error) toast.error(formatApiError(q.error.response?.data?.detail)); }, [q.error]);
  const data = q.data || EMPTY;
  const today = data.today;
  const endDay = end || today;   // poslední den, který je vidět
  // telefon: týden před tím, kam se dá přejet prstem, se načte dopředu
  const qc = useQueryClient();
  useEffect(() => {
    if (!mobile || !endDay) return;
    const r = windowOf(addDays(endDay, -7));
    qc.prefetchQuery({ queryKey: ["diary-overview", r.from, r.to], queryFn: () => load(r), staleTime: 60000 });
  }, [mobile, endDay]); // eslint-disable-line react-hooks/exhaustive-deps

  // Čísla „chybí“ jen za dny, které jsou vidět (PC 14, telefon a tablet 7).
  const days = data.days;
  const at = Object.fromEntries(days.map((d, i) => [d, i]));
  const within = (lo, hi) => (endDay ? days.filter((d) => d > lo && d <= hi) : []);
  const shown = mobile ? within(addDays(endDay, -7), endDay) : days.slice(narrow ? Math.max(0, days.length - 7) : 0);
  const prevDays = mobile ? within(addDays(endDay, -14), addDays(endDay, -7)) : [];
  const nextDays = mobile ? within(endDay, addDays(endDay, 7)) : [];
  const cellsOn = (p, list) => list.map((d) => p.cells[at[d]]);
  const projects = data.projects.map((p) => {
    const cells = cellsOn(p, shown);
    return { ...p, cells, missing: cells.filter((c) => c === "missing").length, prev: cellsOn(p, prevDays), next: cellsOn(p, nextDays) };
  });
  const lastDay = shown[shown.length - 1] || data.date_to;
  const F = useListFilters(projects, {
    groups: [{ key: "zakazka", label: "Zakázka", icon: Briefcase, options: data.projects.map((p) => ({ value: p.project_id, label: p.name })), match: (p, v) => p.project_id === v }],
    search: (p) => p.name, searchTerm: search, entity: { one: "zakázka", few: "zakázky", many: "zakázek" },
  });
  const byProject = F.visible;
  const counts = { all: byProject.length, missing: byProject.filter((p) => p.missing).length, mine: byProject.filter((p) => p.mine).length };
  // server řadí podle názvu, nezahájené dole; Zobrazení je přeskupí (nezahájené zůstanou dole)
  const tabRows = byProject.filter((p) => (view === "missing" ? p.missing : view === "mine" ? p.mine : true));
  const GS = useGroupSort(tabRows, { storageKey: "diary_order", legacyKey: "diary_sort", groups: DIARY_GROUPS, orders: DIARY_ORDERS,
    def: { g: "none", gd: 0, k: "name", dir: "asc" }, tie: byDiaryName, rank: notStartedLast });
  const rows = GS.sorted;
  // na telefonu jdou nezahájené zvlášť pod tabulku (IdleList), skupiny jen z rozjetých
  const mobileGroups = useMemo(() => (GS.groups ? GS.groups.map((g) => ({ ...g, rows: g.rows.filter((p) => !p.not_started) })).filter((g) => g.rows.length) : null), [GS.groups]);
  const missingTotal = rows.reduce((s, p) => s + p.missing, 0);
  const writable = data.projects.filter((p) => !p.not_started);

  const openDay = (pid, date) => nav(`/zakazky/${pid}?tab=diary${date ? `&date=${date}` : ""}`);
  const print = (p) => {
    const photos = (() => { try { return localStorage.getItem("diary_print_photos") === "0" ? "&photos=0" : ""; } catch { return ""; } })();
    const from = shown[0] || data.date_from;   // tiskne se období, které je vidět
    runPrint("diary", `/api/projects/${p.project_id}/diary/export/pdf?date_from=${from}&date_to=${lastDay}${photos}`,
      `stavebni_denik_${from}_${lastDay}.pdf`, "Připravuji PDF deníku…");
  };
  const shift = (n) => {
    const next = addDays(endDay, n);
    setEnd(next >= today ? null : next);
  };

  const monthLabel = lastDay ? `${MONTHS[asDate(lastDay).getMonth()]} ${asDate(lastDay).getFullYear()}` : "";
  const step = narrow ? 7 : 14;
  const windowTitle = !end ? `Posledních ${step} dní` : shown.length ? `${asDate(shown[0]).getDate()}. ${asDate(shown[0]).getMonth() + 1}. – ${asDate(lastDay).getDate()}. ${asDate(lastDay).getMonth() + 1}.` : "";

  // Zapsat dnešek a Tisk: u jedné zakázky rovnou, jinak výběr zakázky.
  const one = writable.length === 1 ? writable[0] : null;
  const projectItems = (fn, testid) => writable.map((p) => ({ label: p.name, onClick: () => fn(p), testid: `${testid}-${p.project_id}` }));

  return (
    <div className="space-y-5" data-testid="diary-overview-page">
      <ListHeader title={mobile ? "Deník" : "Stavební deník"} mobile={mobile} testid="diary-header"
        summary={monthLabel && <span data-testid="diary-subtitle">{monthLabel} · {missingTotal
          ? <span className="text-destructive font-semibold">{missingTotal} {daysWord(missingTotal)} bez zápisu</span>
          : <span className="text-success font-semibold">vše zapsáno</span>}</span>}
        search={search} onSearch={setSearch} searchPh="Hledat zakázku…"
        primary={writable.length ? { label: "Zapsat dnešek", full: "Zapsat dnešek", icon: Pencil, testid: "diary-today-btn",
          ...(one ? { onClick: () => openDay(one.project_id, today) } : { menu: projectItems((p) => openDay(p.project_id, today), "diary-today-btn") }) } : null}
        moreBusy={!!printing}
        more={writable.length ? [one
          ? { icon: Printer, label: "Tisk deníku", hint: "PDF za dny, které jsou vidět", onClick: () => print(one), testid: "diary-print-btn" }
          : { icon: Printer, label: "Tisk deníku — zakázka", sub: projectItems(print, "diary-print-btn"), testid: "diary-print-btn" }] : []} />

      <ListToolbar mobile={mobile} testid="diary-toolbar"
        tabs={[{ value: "all", label: "Všechny zakázky", short: "Všechny", count: counts.all }, { value: "missing", label: "Chybí zápis", count: counts.missing }, { value: "mine", label: "Moje", count: counts.mine }]}
        tab={view} onTab={setView} filter={F} search={search} onSearch={setSearch} searchPh="Hledat zakázku…"
        sort={GS.sortProp} />

      {q.isLoading ? (
        <div className="bg-card border border-border rounded-[20px] p-8 text-center text-muted-foreground">Načítám…</div>
      ) : !rows.length ? (
        <div className="bg-card border border-border rounded-[20px] p-8 text-center text-muted-foreground" data-testid="diary-empty">
          {view === "missing" ? "Žádná zakázka nemá chybějící zápis." : view === "mine" ? "Nemáš žádnou zakázku." : "Žádné rozjeté zakázky."}
        </div>
      ) : mobile ? (() => {
        const nav7 = <WeekNav title={windowTitle} onPrev={() => shift(-step)} onNext={() => shift(step)} canNext={!!end} onToday={end ? () => setEnd(null) : null}
          extra={<InfoTip label="Co znamenají políčka" text={<Legend className="!gap-y-2.5" />} />} />;
        return <MobileGrid rows={rows.filter((p) => !p.not_started)} idle={rows.filter((p) => p.not_started)} shown={shown} prevDays={prevDays} nextDays={nextDays} today={today} openDay={openDay} nav7={nav7}
          onPrev={() => shift(-step)} onNext={() => shift(step)} canNext={!!end} groups={mobileGroups} closed={GS.closed} onToggle={GS.toggle} />;
      })() : (
        <section className="bg-card border border-border rounded-[20px] overflow-hidden" data-testid="diary-matrix">
          <div className="flex items-center justify-between gap-4 px-6 pt-5 pb-4 flex-wrap">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold mr-1" data-testid="diary-window-title">{windowTitle}</h2>
              <button type="button" onClick={() => shift(-step)} aria-label={`Předchozích ${step} dní`} title={`Předchozích ${step} dní`} data-testid="diary-prev"
                className="w-8 h-8 rounded-full border border-border grid place-items-center text-muted-foreground hover:text-foreground"><ChevronLeft size={16} /></button>
              <button type="button" onClick={() => shift(step)} disabled={!end} aria-label={`Dalších ${step} dní`} title={`Dalších ${step} dní`} data-testid="diary-next"
                className="w-8 h-8 rounded-full border border-border grid place-items-center text-muted-foreground hover:text-foreground disabled:opacity-30"><ChevronRight size={16} /></button>
              {end && <button type="button" onClick={() => setEnd(null)} className="h-8 px-3 rounded-full border border-border text-[13px] font-semibold hover:text-brand">Dnes</button>}
            </div>
            <Legend />
          </div>
          <div className="overflow-x-auto">
            <div className={`grid ${narrow ? "" : "min-w-[760px]"}`} style={{ gridTemplateColumns: `minmax(${narrow ? 180 : 260}px,3fr) repeat(${shown.length},minmax(34px,1fr)) ${narrow ? 96 : 120}px` }}>
              <div className="px-5 lg:px-6 py-2 text-[12px] font-semibold text-muted-foreground border-y border-border">Zakázka</div>
              {shown.map((d) => (
                <div key={d} className={`py-2 text-center border-y border-border text-[11px] font-semibold leading-tight ${d === today ? "text-brand font-bold bg-brand/10" : "text-muted-foreground"}`}>
                  {DOW[asDate(d).getDay()]}<br /><span className="tabular-nums text-[12px]">{asDate(d).getDate()}.</span>
                </div>
              ))}
              <div className="py-2 pr-6 text-right text-[12px] font-semibold text-muted-foreground border-y border-border">Chybí</div>
              {(GS.groups ? GS.groups.flatMap((g) => [
                <div key={`g-${g.key}`} style={{ gridColumn: "1 / -1" }}><GroupHeader g={g} closed={GS.closed.has(g.key)} onToggle={GS.toggle} /></div>,
                ...(GS.closed.has(g.key) ? [] : g.rows)]) : rows).map((p) => (!p.project_id ? p : (
                <div key={p.project_id} className="contents" data-testid={`diary-row-${p.project_id}`}>
                  <button type="button" onClick={() => openDay(p.project_id)} title={`Otevřít deník — ${p.name}`}
                    className="px-5 lg:px-6 py-2.5 flex items-center gap-2.5 min-w-0 border-b border-border text-left hover:text-brand">
                    <Thumb id={p.project_id} small /><span className="font-semibold text-[14px] truncate">{p.name}</span>
                  </button>
                  {p.cells.map((st, i) => {
                    const d = shown[i];
                    const tip = `${STATE[st]?.label || ""} · ${dLabel(d)}`;
                    return (
                      <div key={d} className={`grid place-items-center border-b border-border ${d === today ? "bg-brand/10" : ""}`}>
                        {st === "off" ? <span title={tip}><Mark state={st} /></span> : (
                          <button type="button" onClick={() => openDay(p.project_id, d)} title={tip} aria-label={`${p.name} — ${tip}`}
                            className="rounded-[9px] hover:ring-2 hover:ring-brand/40 focus-visible:ring-2 focus-visible:ring-brand outline-none" data-testid={`diary-cell-${p.project_id}-${d}`}>
                            <Mark state={st} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                  <div className="pr-6 flex items-center justify-end border-b border-border"><MissingPill p={p} /></div>
                </div>
              )))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
